import { useCallback, useEffect, useState } from 'react';
import { List } from '@mui/material';
import { ListWrapper, Scroller, SectionLabel, Wrapper } from '@styles/Admin-tools/Roles/RoleModalStyles';
import { handleAllUsersCheck, toggleTeamType } from '../../utils';
import { IRoleAssignmentSelection } from '../../models/roleAssignmentSelectionModel';
import { allMemberTypes } from '../../models/RoleAssignmentModel';
import { SelectableListItem } from './components/SelectableListItem';
import { TeamTypeSection } from './TeamTypeSection';
import { SelectableCollapse } from './components/SelectableCollapse';

interface IGroupsOfUsersProps {
  selectionModel: IRoleAssignmentSelection;

  onModelChange(model: IRoleAssignmentSelection): void;
}

type TIncludeRoleTypes = 'includeAllManagers' | 'includeAllDirectors' | 'includeAllLieutenants';

export const GroupsOfUsers = ({ selectionModel, onModelChange }: IGroupsOfUsersProps) => {
  const [localState, setLocalState] = useState<IRoleAssignmentSelection>(selectionModel);

  useEffect(() => {
    selectionModel && setLocalState(JSON.parse(JSON.stringify(selectionModel)));
  }, [selectionModel]);

  const toggleAllCheckboxes = useCallback(() => {
    const value = !localState.allUsers;
    localState.allUsers = value;
    localState.includeAllManagers = value;
    localState.includeAllDirectors = value;
    localState.includeAllLieutenants = value;
    localState.teamType = localState.teamType.map((teamType) => toggleTeamType(teamType, value));

    onModelChange(localState);
  }, [localState, onModelChange]);

  const onToggleFullTeamType = useCallback((teamTypeId: string) => {
    localState.teamType = localState.teamType.map((type) => {
      const teamMatched = type.teamTypeId === teamTypeId;
      const nextValue = teamMatched ? !type.includeFullTeamType : type.includeFullTeamType;
      return teamMatched ? {
        ...type,
        includeFullTeamType: nextValue,
        includeManager: nextValue,
        includedTeamMemberType: nextValue ? allMemberTypes : [],
        teams: type.teams.map((team) => ({
          ...team,
          includeFullTeam: nextValue,
          includedTeamMemberType: nextValue ? allMemberTypes : [],
        })),
      } : type;
    });

    handleAllUsersCheck(localState, onModelChange);
  }, [localState, handleAllUsersCheck, onModelChange]);

  const onChangeIncludedRoleType = useCallback((key: TIncludeRoleTypes) => {
    localState[key] = !localState[key];
    handleAllUsersCheck(localState, onModelChange);
  }, [localState, handleAllUsersCheck, onModelChange]);

  return (
    <Wrapper>
      <SectionLabel>Groups of Users</SectionLabel>

      <ListWrapper>
        <Scroller>
          <SelectableCollapse open text="All Users" checked={localState.allUsers} onChange={toggleAllCheckboxes}>
            <List>
              {localState.teamType.map((teamType) => (
                <TeamTypeSection
                  key={teamType.teamTypeId}
                  teamType={teamType}
                  localState={localState}
                  onModelChange={onModelChange}
                  onToggleFullTeamType={() => onToggleFullTeamType(teamType.teamTypeId)}
                />
              ))}

              <SelectableListItem
                text="All Team Managers"
                checked={localState.allUsers || localState.includeAllManagers}
                onSelectionChange={() => onChangeIncludedRoleType('includeAllManagers')}
              />

              <SelectableListItem
                text="All Team Directors"
                checked={localState.allUsers || localState.includeAllDirectors}
                onSelectionChange={() => onChangeIncludedRoleType('includeAllDirectors')}
              />

              <SelectableListItem
                text="All Team Lieutenants"
                checked={localState.allUsers || localState.includeAllLieutenants}
                onSelectionChange={() => onChangeIncludedRoleType('includeAllLieutenants')}
              />
            </List>
          </SelectableCollapse>
        </Scroller>
      </ListWrapper>
    </Wrapper>
  );
};
